import moment from "moment";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../parts/SvgIcon";
import FlexibleProgramPreviewStrip from "./components/FlexibleProgramPreviewStrip";

const pretty = (yyyymmdd) =>
  yyyymmdd ? moment(String(yyyymmdd), "YYYYMMDD").format("MMM D, YYYY") : "";

/**
 * Read-only, rolling six-week look at one client's Flexible Program: what each
 * week will hand the client and, for slots they've already done, what they
 * actually logged.
 *
 * Same page root as FlexibleProgram (teams-manager + flexible-manager) so the
 * header, top panel and exercise-frame/set-dot styles apply unchanged - those
 * live in teams-manager.css scoped under `.teams-manager`.
 */
const FlexibleProgramPreview = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const cycle = locals?.editCycle;
  const athlete = locals?.query?.athlete;
  const weekOffset = locals?.weekOffset || 0;
  const previewWindow = locals?.previewWindow || { cards: [] };
  const { cards, disableBack, disableForward } = previewWindow;

  const organizationCoach = locals?.query?.organizationCoach;
  const managerPath = `/athlete-portal/programs/flexible-program/${cycle?._id}${athlete ? `?athlete=${athlete}${organizationCoach ? `&organizationCoach=${organizationCoach}` : ""}` : ""}`;

  // /actions/refresh merges into the referrer's query string, so ?athlete=...
  // rides along without being re-appended here.
  const stepHref = (delta) =>
    `/actions/refresh?weekOffset=${weekOffset + delta}`;

  return (
    <main className="teams-manager flexible-manager flexible-program-preview">
      <header>
        <hgroup>
          <a href={managerPath}>
            <button type="button" data-button-type="tertiary" data-icon-only>
              <SvgIcon src="/img/new-icons/arrow-square-left.svg" />
            </button>
            <span className="text-nowrap pr-4">{cycle?.name}</span>
          </a>
        </hgroup>
      </header>

      <div className="team-tab-container">
        <section className="flexible-top-panel">
          <div className="flexible-panel-row">
            <div className="flex flex-col gap-1">
              <span className="s1">{t("flexible.preview.title")}</span>
              {cards.length > 0 && (
                <span className="p4 opacity-70">
                  {pretty(cards[0].cardStart)} &ndash;{" "}
                  {pretty(cards[cards.length - 1].cardEnd)}
                </span>
              )}
            </div>

            {cards.length > 0 && (
              <div className="flex items-center gap-2">
                {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
                <a
                  role="button"
                  data-button-type="secondary"
                  data-icon
                  data-icon-only
                  href={stepHref(-1)}
                  aria-disabled={disableBack}
                  aria-label={t("flexible.preview.back")}
                  className={
                    disableBack ? "opacity-40 pointer-events-none" : ""
                  }
                  onClick={(event) => {
                    if (disableBack) event.preventDefault();
                  }}
                >
                  <SvgIcon src="/img/new-icons/arrow-left.svg" />
                </a>
                {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
                <a
                  role="button"
                  data-button-type="secondary"
                  href="/actions/refresh?weekOffset=0"
                  aria-disabled={weekOffset === 0}
                  className={
                    weekOffset === 0 ? "opacity-40 pointer-events-none" : ""
                  }
                  onClick={(event) => {
                    if (weekOffset === 0) event.preventDefault();
                  }}
                >
                  {t("flexible.preview.thisWeek")}
                </a>
                {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
                <a
                  role="button"
                  data-button-type="secondary"
                  data-icon
                  data-icon-only
                  href={stepHref(1)}
                  aria-disabled={disableForward}
                  aria-label={t("flexible.preview.forward")}
                  className={
                    disableForward ? "opacity-40 pointer-events-none" : ""
                  }
                  onClick={(event) => {
                    if (disableForward) event.preventDefault();
                  }}
                >
                  <SvgIcon src="/img/new-icons/arrow-right.svg" />
                </a>
              </div>
            )}
          </div>
          <p className="p4 opacity-70">{t("flexible.preview.hint")}</p>
        </section>

        <FlexibleProgramPreviewStrip locals={locals} api={api} />
      </div>
    </main>
  );
};

export default FlexibleProgramPreview;
